import React, { useRef, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { productBulkUploadAPI } from "../../config";
import Layout from "../../layout/Layout";
import moment from "moment";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import { AlertMessage } from "../../components/AlertMessage";

import { read, utils } from "xlsx";
import { fitColumns } from "../../utils/excelFit";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { download, generateCsv, mkConfig } from "export-to-csv";

import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Breadcrumbs,
  Link,
  Chip,
  CircularProgress,
  Backdrop,
  Tooltip,
} from "@mui/material";
import * as Yup from "yup";
import { MaterialReactTable } from "material-react-table";
import { apiData } from "../../services/api_service";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

const BulkUpload = (props) => {
  const user = JSON.parse(localStorage.getItem("user"));

  const alertRef = useRef();

  const navigate = useNavigate();
  const [showStatus, setShowStatus] = useState(false);

  const [successCount, setSuccessCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [productList, setProductList] = useState([]);
  const [data, setData] = useState([]);

  const validationSchema = Yup.object({
    product_name: Yup.string()
      .required("Product Name is required")
      .min(3, "Product Name must be at least 3 characters")
      .max(100, "Product Name cannot exceed 100 characters"),

    color: Yup.string()
      .required("Color is required")
      .min(3, "Color must be at least 3 characters")
      .max(100, "Color cannot exceed 100 characters"),

    size: Yup.string()
      .required("Size is required")
      .min(3, "Size must be at least 3 characters")
      .max(100, "Size cannot exceed 100 characters"),

    bar_code: Yup.string()
      .required("Barcode is required")
      .min(3, "Barcode must be at least 3 characters")
      .max(100, "Barcode cannot exceed 100 characters"),

    sku: Yup.string()
      .required("SKU is required")
      .matches(
        /^[a-zA-Z0-9-_]+$/,
        "SKU can only contain letters, numbers, hyphens, and underscores"
      ),

    category: Yup.string()
      .required("Category is required")
      .typeError("Category must be text"),

    uom: Yup.string().required("UOM is required").typeError("UOM must be text"),

    // selling_price: Yup.number()
    //   .typeError("Selling Price must be a valid number")
    //   .required("Selling Price is required")
    //   .moreThan(
    //     Yup.ref("buying_price"),
    //     "Selling Price must be greater than Buying Price"
    //   ),

    // buying_price: Yup.number()
    //   .typeError("Buying Price must be a valid number")
    //   .required("Buying Price is required")
    //   .min(0, "Buying Price cannot be negative"),

    // allow_backorder: Yup.boolean(),

    // backorder_limit: Yup.number().when("allow_backorder", {
    //   is: true,
    //   then: Yup.number()
    //     .typeError("Backorder Limit must be a number")
    //     .required("Backorder Limit is required")
    //     .min(0, "Backorder Limit cannot be negative"),
    //   otherwise: Yup.number().notRequired(),
    // }),

    description: Yup.string().max(
      500,
      "Description cannot exceed 500 characters"
    ),
  });

  const [showValidationColumn, setShowValidationColumn] = useState(false);

  const handleExportData = () => {
    if (!productList.length) return;

    // Map the data as-is
    const exportData = productList.map((row) => ({
      SKU: row.sku || "",
      "Product Name": row.product_name || "",
      Category: row.category || "",
      // "Buying Price": row.buying_price || "",
      // "Selling Price": row.selling_price || "",
      "Description (Optional)": row.description || "",
      UOM: row.uom || "",
      Color: row.color || "",
      Size: row.size || "",
      Barcode: row.bar_code || "",
      // Same headers as the upload template, so an exported file can be uploaded again.
      "Allow Backorder (Yes/No)": row.allow_backorder === 1 || row.allow_backorder === "Yes" ? "Yes" : "No",
      "Backorder Limit": row.backorder_limit ?? "",
    }));

    const csvConfig = mkConfig({
      fieldSeparator: ",",
      decimalSeparator: ".",
      useKeysAsHeaders: true,
      filename: "Product_List",
    });

    const csv = generateCsv(csvConfig)(exportData);
    download({
      ...csvConfig,
      filename: "Product_List.csv",
    })(csv);
  };

  const ExportTemplate = () => {
    const columnHeaders = [
      [
        "Product Name",
        "UOM",
        "SKU",

        "Category",
        "Color",
        "Size",
        "Barcode",
        "Description (Optional)",

        "Allow Backorder (Yes/No)", // <-- instruction added
        "Backorder Limit",
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(columnHeaders);
    const wb = XLSX.utils.book_new();
    fitColumns(ws);
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    writeXlsxFrozen(XLSX, wb, "Product_Template.xlsx");
  };

  const handleSubmit = async () => {
    setLoading(true);
    setShowStatus(true);

    const updatedData = productList.map((obj) => ({
      ...obj,
      DOJ: moment(obj["DOJ"]).format("YYYY-MM-DD"),
      Status: 1,
      created_by: user.emp_id,
    }));

    const payload = { data: updatedData };

    const result = await apiData("post", productBulkUploadAPI, payload);

    if (result.data) {
      const { success: totalSuccess, failed, responses } = result.data;

      // Update counts
      setSuccessCount(totalSuccess);
      setPendingCount(failed);

      // Keep only pending rows in table
      const failedItems = responses
        .filter((r) => !r.success)
        .map((r) => r.item);
      setProductList(failedItems);

      // Show alert:
      // If any failed items, show first error message
      const firstError = responses.find((r) => !r.success);
      const message = firstError
        ? `Error: ${firstError.item.product_name || firstError.item.sku} - ${
            firstError.message
          }`
        : "All products uploaded successfully";

      alertRef.current.triggerAlert(
        JSON.stringify({
          message,
          messageType: failed > 0 ? "warning" : "success",
        })
      );
    } else {
      // fallback if something went wrong
      alertRef.current.triggerAlert(
        JSON.stringify({
          message: result.msg || "An unexpected error occurred",
          messageType: "error",
        })
      );
    }

    setLoading(false);
  };

  const handleFileUpload = async ($event) => {
    setLoading(true);
    setProductList([]);
    setPendingCount(0);
    setSuccessCount(0);

    const files = $event.target.files;
    const file = files[0];
    if (!file) {
      setLoading(false);
      return;
    }

    // ✅ Allowed file types
    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
      "text/csv", // .csv
    ];

    if (!allowedTypes.includes(file.type)) {
      setLoading(false);
      alertRef.current.triggerAlert(
        JSON.stringify({
          message: "Invalid file type. Please upload an Excel or CSV file.",
          messageType: "error",
        })
      );
      $event.target.value = "";

      return;
    }

    if (files) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const wb = read(event.target.result);
        const sheets = wb.SheetNames;

        if (sheets.length) {
          const rows = utils.sheet_to_json(wb.Sheets[sheets[0]], {
            raw: false,
            dateNF: "yyyy-mm-dd",
            cellDates: true,
          });

          const headerMap = {
            SKU: "sku",
            "Product Name": "product_name",
            Category: "category",
            Color: "color",
            Size: "size",
            Barcode: "bar_code",
            // "Buying Price": "buying_price",
            // "Selling Price": "selling_price",

            // "Allow Backorder (Yes/No)": "allow_backorder",
            // "Backorder Limit": "backorder_limit",
            "Description (Optional)": "description",

            UOM: "uom",
          };

          const updatedData = rows.map((row) => {
            const mappedRow = {};
            for (let key in row) {
              const newKey = headerMap[key] || key;
              mappedRow[newKey] = row[key];
            }

            // ✅ Normalize allow_backorder / out_of_stock
            // if (mappedRow.allow_backorder) {
            //   mappedRow.allow_backorder =
            //     mappedRow.allow_backorder.toString().toLowerCase() === "yes"
            //       ? 1
            //       : 0;
            // }

            return mappedRow;
          });

          const validatedData = await Promise.all(
            updatedData.map(async (item) => {
              try {
                await validationSchema.validate(item, { abortEarly: false });
                return { ...item, errorMessages: null };
              } catch (err) {
                return {
                  ...item,
                  errorMessages: err.errors.join(", "),
                };
              }
            })
          );
          setProductList(validatedData);
          const hasErrors = validatedData.some((row) => row.errorMessages);
          setShowValidationColumn(hasErrors);
        }
        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const tableRef = useRef();

  // useEffect(() => {
  //   if (productList.length > 0) {
  //     // Find first row with error
  //     const firstErrorIndex = productList.findIndex((row) => row.errorMessages);
  //     if (firstErrorIndex !== -1 && tableRef.current) {
  //       // Scroll to last column (validation column)
  //       const table = tableRef.current;
  //       const row = table.querySelectorAll("tbody tr")[firstErrorIndex];
  //       if (row) {
  //         const lastCell = row.lastChild;
  //         lastCell.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  //       }
  //     }
  //   }
  // }, [productList]);

  //   const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  const columns = useMemo(() => {
    const baseColumns = [
      { accessorKey: "product_name", header: "Product Name", size: 200 },
      { accessorKey: "uom", header: "UOM", size: 100 },

      { accessorKey: "sku", header: "SKU", size: 150 },
      { accessorKey: "category", header: "Category", size: 150 },
      { accessorKey: "color", header: "Color", size: 150 },
      { accessorKey: "size", header: "Size", size: 150 },
      { accessorKey: "bar_code", header: "Barcode", size: 150 },
      // { accessorKey: "buying_price", header: "Buying Price", size: 120 },
      // { accessorKey: "selling_price", header: "Selling Price", size: 120 },
      {
        accessorKey: "description",
        header: "Description (Optional)",
        size: 200,
      },
    ];

    // ✅ Add validation column only if any errors
    if (showValidationColumn) {
      baseColumns.push({
        accessorKey: "errorMessages",
        header: "Validation Status",
        size: 200,
        // ✅ Sticky body cells
        muiTableBodyCellProps: {
          sx: {
            position: "sticky",
            right: 0,
            backgroundColor: "#fff",
            zIndex: 1,
          },
        },
        // ✅ Sticky header
        muiTableHeadCellProps: {
          sx: {
            position: "sticky",
            right: 0,
            backgroundColor: "#f5f5f5", // optional: distinguish header
            zIndex: 2,
          },
        },
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <span style={{ color: value ? "red" : "green" }}>
              {value || "No errors"}
            </span>
          );
        },
      });
    }

    return baseColumns;
  }, [showValidationColumn]);

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <AlertMessage ref={alertRef} />

        <Grid container spacing={3}>
          <Grid item xs={12} lg={6} md={6} textAlign="left">
            <Typography className="page_title">Add Product</Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Product</Typography>
              <Typography color="text.primary">Bulk Upload</Typography>
            </Breadcrumbs>
          </Grid>

          <Grid item xs={12} lg={6} md={6} textAlign="right">
            <Button
              variant="contained"
              color="secondary"
              href="/product/list"
              className="button_first"
            >
              List Products
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
              sx={{ ml: 2 }}
            >
              Back
            </Button>
          </Grid>
        </Grid>

        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backdropFilter: "blur(4px)", // ✅ adds blur effect
            backgroundColor: "rgba(0,0,0,0.4)", // dimmed background
          }}
          open={loading}
        >
          <div style={{ textAlign: "center" }}>
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Adding Products, Please wait...
            </Typography>
          </div>
        </Backdrop>

        <>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              {/* <TextField type="file" onChange={handleFileUpload} /> */}
              <TextField
                type="file"
                name="file"
                className="custom-file-input"
                id="inputGroupFile"
                required
                onChange={handleFileUpload}
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              />

              <Button
                onClick={ExportTemplate}
                style={{ background: "#1a73e829" }}
                // sx={{ ml: 8 }}
                startIcon={<FileDownload />}
              >
                Export Template
              </Button>
            </Grid>

            {/* <TextField type="file" onChange={handleFileUpload} /> */}
            {productList.length > 0 && (
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <Box display={"flex"} gap={1}>
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={productList.some((item) => item.errorMessages)}
                    sx={{ mt: 3, mb: 2 }}
                    onClick={() => {
                      handleSubmit();
                    }}
                  >
                    Submit
                  </Button>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} display="flex" width={0}>
              <MaterialReactTable
                columns={columns}
                enableFullScreenToggle={false}
                data={productList}
                getRowId={(row) => row.name}
                enableGlobalFilter={false}
                enableRowNumbers
                rowNumberMode="original"
                enableColumnActions={false}
                enableColumnFilters={false}
                enablePagination={false}
                enableSorting={false}
                enableStickyHeader
                enableRowVirtualization={false}
                muiTableContainerProps={{
                  sx: {
                    maxHeight: 400, // set your desired height
                    overflowY: "auto", // enable vertical scroll inside table
                  },
                }}
                rowVirtualizerOptions={{ overscan: 5 }}
                //   initialState={{
                //     columnPinning: lgUp ? { right: ['Status'] } : {},
                //   }}
                renderTopToolbarCustomActions={({ table }) => (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "1rem",
                        p: "0.5rem",
                        justifyContent: "space-between",
                        flexGrow: 1,

                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {/* RIGHT SIDE BUTTON */}
                        <Tooltip title="Export data to excel sheet" arrow>
                          <Button
                            onClick={handleExportData}
                            style={{ background: "#1a73e829" }}
                            startIcon={<FileDownload />}
                            // startIcon={<IconTableExport />}
                          >
                            Export To Excel
                          </Button>
                        </Tooltip>
                      </Box>

                      {productList.length > 0 && showStatus && (
                        <Box display={"flex"} alignItems="center" gap={1}>
                          <Chip
                            label={`Added: ${successCount}`}
                            color="success"
                            variant="outlined"
                          />
                          <Chip
                            label={`Pending: ${pendingCount}`}
                            color="warning"
                            variant="outlined"
                          />
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              />
            </Grid>
          </Grid>
        </>
      </Grid>
    </Layout>
  );
};

export default BulkUpload;
